import type { TaskType } from "@common/types/TaskTypes";
import { TaskTypes } from "@common/types/TaskTypes";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import { Alert } from "components/core/Alert";
import Checkbox from "components/core/Checkbox";
import DateStringPicker from "components/core/DateStringPicker";
import { Flex } from "components/core/Flex";
import { Text } from "components/core/Text";

import { useCompletionFormContext } from "./useCompletionFormContext";

const LEARN_MORE_URL = "https://help.seedtime.us/en/articles/5833556-using-the-task-list";

const MOVES_DEPENDENT_TASKS = new Set<string>([
  TaskTypes.DirectSeeding,
  TaskTypes.IndoorSeeding,
  TaskTypes.Transplanting
]);

type Props = {
  taskType: TaskType;
};

const disableFutureDates = (current: Dayjs) => current.isAfter(dayjs(), "day");

const OverdueSection = ({ taskType }: Props) => {
  const { watch, setValue } = useCompletionFormContext();
  const overdueAcknowledged = watch("overdueAcknowledged");
  const completedAt = watch("completedAt");

  return (
    <Flex direction="col" gap={4}>
      <Alert
        showIcon
        title={
          <Flex align="center" gap={2} justify="between">
            <span>
              {MOVES_DEPENDENT_TASKS.has(taskType)
                ? "This task is overdue. Completing it will move this task and its dependent tasks to match the completion date you select below."
                : "This task is overdue. Completing it will move this task to match the completion date you select below."}
            </span>
            <a
              className="shrink-0"
              href={LEARN_MORE_URL}
              rel="noreferrer"
              target="_blank"
            >
              Learn more
            </a>
          </Flex>
        }
        variant="warning"
      />
      <Flex align="center" gap={2}>
        <Text variant="bodySmall">Date completed</Text>
        <DateStringPicker
          allowClear={false}
          disabledDate={disableFutureDates}
          value={completedAt}
          onChange={(value) => setValue("completedAt", value)}
        />
      </Flex>
      <Flex justify="end">
        <Checkbox
          checked={overdueAcknowledged}
          onCheckedChange={(checked) => setValue("overdueAcknowledged", checked)}
        >
          I understand, proceed with completion <Text color="destructive">*</Text>
        </Checkbox>
      </Flex>
    </Flex>
  );
};

export default OverdueSection;
